import React, { useState } from "react";
import {
  Card,
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { PendingOrderPage } from "./PendingOrderPage";

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB");
};

// Maps the legacy PascalCase fields from BC to a flat row consumed by the
// detail page. Kept stable to avoid touching PendingOrderPage.
const mapRow = (it) => ({
  no: it.no,
  documentNumber: it.systemId,
  documentType: it.documentType,
  selltoCustomerNo: it.selltoCustomerNo,
  selltoCustomerName: it.selltoCustomerName,
  postingDate: it.postingDate,
  vehicleRegistrationNumber: it.VehicleRegistrationNo,
  location: it.Location,
  locationCode: it.LocationCode,
  fitterName: it.fitterName,
  fitterNo: it.fitterNo,
  serviceType: it.serviceType,
  status: it.status,
  jobStatus: it.JobStatus,
  mileage: it.Mileage,
  docketNo: it.DocketNo,
  reTorque: it.ReTorque,
  salesPersonCode: it.salespersonCode,
  tyreCheck: it.tyreCheck,
});

export const PendingOrdersList = (props) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedItem, setSelectedItem] = useState(null);

  const items = props.orders?.result || [];

  if (props.showItem) {
    return (
      <PendingOrderPage
        onBack={() => props.setShowItem(false)}
        item={selectedItem}
      />
    );
  }

  const handleOpen = (it) => {
    setSelectedItem(mapRow(it));
    props.setShowItem(true);
  };

  const columns = isCompact
    ? [
        { key: "no", label: "No" },
        { key: "postingDate", label: "Date" },
        { key: "serviceType", label: "Service" },
        { key: "status", label: "Status" },
      ]
    : [
        { key: "no", label: "No" },
        { key: "postingDate", label: "Date" },
        { key: "vehicleRegistrationNumber", label: "Reg" },
        { key: "serviceType", label: "Service" },
        { key: "location", label: "Location" },
        { key: "locationCode", label: "Warehouse" },
        { key: "fitterName", label: "Fitter" },
        { key: "status", label: "Status" },
      ];

  return (
    <Card
      sx={{
        overflow: "hidden",
        border: (t) => `1px solid ${t.palette.divider}`,
        boxShadow: "none",
      }}
    >
      <TableContainer>
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: 12,
                    letterSpacing: 0.5,
                    bgcolor: "background.default",
                  }}
                >
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it, idx) => (
              <TableRow
                key={`${it.no}-${idx}`}
                hover
                onClick={() => handleOpen(it)}
                sx={{
                  cursor: "pointer",
                  "&:last-of-type td": { borderBottom: 0 },
                }}
              >
                <TableCell>
                  <MuiLink
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: isCompact ? 13 : 14,
                    }}
                  >
                    {it.no}
                  </MuiLink>
                </TableCell>
                <TableCell sx={{ fontSize: isCompact ? 13 : 14 }}>
                  {formatDate(it.postingDate)}
                </TableCell>
                {!isCompact && (
                  <TableCell sx={{ fontSize: 14 }}>
                    {it.VehicleRegistrationNo || "—"}
                  </TableCell>
                )}
                <TableCell sx={{ fontSize: isCompact ? 13 : 14 }}>
                  {it.serviceType ? it.serviceType.trim() : "—"}
                </TableCell>
                {!isCompact && <TableCell>{it.Location || "—"}</TableCell>}
                {!isCompact && (
                  <TableCell>{it.LocationCode || "—"}</TableCell>
                )}
                {!isCompact && (
                  <TableCell>{it.fitterName || "—"}</TableCell>
                )}
                <TableCell>
                  {it.status ? (
                    <Chip
                      size="small"
                      label={it.status}
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: "rgba(245, 166, 35, 0.18)",
                        color: "text.primary",
                      }}
                    />
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  <Typography variant="body2">No pending orders to show</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};
